"use client"

import { useState, useEffect, useCallback } from "react"
import { useRouter } from "next/navigation"
import { motion, AnimatePresence } from "framer-motion"
import Image from "next/image"
import {
  Volume2, Mic, Globe, Heart, ShieldOff, Clock,
  RefreshCw, Sparkles, TrendingUp,
  MessageCircle, Languages, BarChart3,
  CheckCircle, ArrowRight, Check,
  ChevronDown,
} from "lucide-react"
import { useAuth } from "../context/AuthContext"
import Logo from "../components/Logo"
import WhatsAppWidget from "../components/WhatsAppWidget"
import ScrollDepthTracker from "../components/ScrollDepthTracker"
import GeneralRosetVsGpt from "../components/GeneralRosetVsGpt"
import { trackEvent } from "../lib/analytics"
import ThemeToggle from "../components/ThemeToggle"

// ─── TopMenu ────────────────────────────────────────────────
function TopMenuES() {
  const [isOpen, setIsOpen] = useState(false)
  const router = useRouter()
  const { user } = useAuth()

  const handleLogin = useCallback(async () => {
    if (user) { router.push("/app"); return }
    try {
      const { supabase } = await import("../lib/supabase")
      const redirectTo = `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo },
      })
      if (error) console.error("Google login error:", error.message)
    } catch (err) {
      console.error("Login error:", err)
    }
  }, [user, router])

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
    setIsOpen(false)
  }

  const navItems = [
    { label: "Beneficios", action: () => scrollTo("benefits-section") },
    { label: "Preguntas frecuentes", action: () => scrollTo("faq-section") },
    { label: "Contacto", action: () => { window.open("https://wa.me/+4552803868", "_blank", "noopener,noreferrer"); setIsOpen(false) } },
  ]

  return (
    <nav className="w-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-slate-200/60 dark:border-slate-700/60 h-16 flex items-center z-50 sticky top-0">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex justify-between items-center w-full">
        <div className="flex items-center gap-2.5">
          <Logo />
          <span className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Roset</span>
        </div>
        <div className="hidden md:flex items-center gap-7">
          {navItems.map((item, i) => (
            <button key={i} onClick={item.action} className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-sm font-medium transition-colors duration-150">
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2.5">
          {!user && (
            <button onClick={() => { trackEvent("cta_click", { location: "nav" }); router.push("/app") }}
              className="hidden sm:inline-flex px-4 py-1.5 text-sm font-semibold rounded-full bg-blue-600 text-white hover:bg-blue-500 transition duration-150">
              Empieza Gratis
            </button>
          )}
          <ThemeToggle />
          <button onClick={handleLogin}
            className="px-4 py-1.5 text-sm font-medium rounded-full text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition duration-150">
            {user ? "Abrir App" : "Iniciar sesión"}
          </button>
          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden flex flex-col justify-center items-center w-8 h-8 gap-1">
            <span className={`w-5 h-0.5 bg-slate-600 dark:bg-slate-300 transition-all duration-200 ${isOpen ? "rotate-45 translate-y-1.5" : ""}`} />
            <span className={`w-5 h-0.5 bg-slate-600 dark:bg-slate-300 transition-all duration-200 ${isOpen ? "opacity-0" : ""}`} />
            <span className={`w-5 h-0.5 bg-slate-600 dark:bg-slate-300 transition-all duration-200 ${isOpen ? "-rotate-45 -translate-y-1.5" : ""}`} />
          </button>
        </div>
      </div>
      {isOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {navItems.map((item, i) => (
              <button key={i} onClick={item.action}
                className="block w-full text-left text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 font-medium py-2.5 px-3 rounded-lg text-sm transition-colors duration-150">
                {item.label}
              </button>
            ))}
            {!user && (
              <button onClick={() => { trackEvent("cta_click", { location: "nav_mobile" }); router.push("/app"); setIsOpen(false) }}
                className="block w-full text-center bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2.5 rounded-full transition duration-150 mt-2">
                Empieza Gratis
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}

// ─── HeroSection ────────────────────────────────────────────
function HeroSectionES() {
  const router = useRouter()
  return (
    <section className="relative bg-white dark:bg-slate-950 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 dark:bg-blue-900/20 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-orange-100/30 dark:bg-orange-900/15 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
      <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] text-gray-900 dark:text-white tracking-tight">
              Habla un nuevo idioma con confianza. Tu tutor IA tiene paciencia infinita.
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.2 }}
              className="text-lg text-gray-500 dark:text-slate-400 mt-6 leading-relaxed max-w-lg">
              No más ejercicios que simulan progreso sin resultados reales. Con Roset hablas desde el primer minuto, cometes errores sin miedo y tu tutor IA te guía hasta que hablar se sienta natural.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}
              className="mt-8 flex flex-col sm:flex-row items-start gap-4">
              <button onClick={() => { trackEvent("cta_click", { location: "hero" }); router.push("/app") }}
                className="group bg-blue-500 hover:bg-blue-600 text-white text-lg font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25 active:scale-[0.98]">
                <span className="flex items-center gap-2">
                  Comienza a Hablar GRATIS
                  <svg className="w-5 h-5 group-hover:translate-x-0.5 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
              <p className="text-sm text-gray-400 dark:text-slate-500 self-center">Sin tarjeta de crédito</p>
            </motion.div>
          </motion.div>
          {/* ── Mobile hero visual ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.35 }}
            className="lg:hidden mt-4">
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-lg shadow-gray-200/50 dark:shadow-slate-900/50">
              <div className="bg-gradient-to-r from-blue-100 to-orange-100 dark:from-blue-900/40 dark:to-orange-900/40 px-4 py-2.5 flex items-center justify-between border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px] font-semibold">AI</div>
                  <div>
                    <p className="text-gray-700 dark:text-slate-200 text-sm font-medium">Lucía</p>
                    <p className="text-gray-500 dark:text-slate-400 text-[10px]">Tutora de español</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="text-gray-500 dark:text-slate-400 text-[10px]">En línea</span>
                </div>
              </div>
              <div className="p-3.5 space-y-3">
                <div className="flex gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center text-[10px] shrink-0 mt-0.5">AI</div>
                  <div className="bg-[#F7F7F8] dark:bg-slate-700 rounded-2xl rounded-tl-md px-3.5 py-2">
                    <p className="text-sm text-gray-900 dark:text-slate-100">¡Hola! ¿Qué hiciste el fin de semana?</p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-[#E8EFFF] dark:bg-blue-900/40 rounded-2xl rounded-tr-md px-3.5 py-2">
                    <p className="text-sm text-[#1D3B9D] dark:text-blue-300">Fui al cine con mis amigos.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center text-[10px] shrink-0 mt-0.5">AI</div>
                  <div className="bg-[#F7F7F8] dark:bg-slate-700 rounded-2xl rounded-tl-md px-3.5 py-2">
                    <p className="text-sm text-gray-900 dark:text-slate-100">¡Genial! ¿Qué película vieron? 🎬</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-1">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-500 flex items-center justify-center shrink-0">
                    <Mic className="w-3 h-3" />
                  </div>
                  <div className="flex-1 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-full px-3 py-2 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                    <span className="text-xs text-gray-500 dark:text-slate-400">Escuchando...</span>
                    <div className="flex gap-px items-end ml-auto">
                      {[3,6,4,8,5,7,4,9,3,6,5,3].map((h,i) => (
                        <motion.div key={i} className="w-0.5 bg-blue-400/60 rounded-full"
                          animate={{ height: [h, h + 4, h] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.08 }}
                          style={{ height: `${h}px` }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center gap-3 mt-4 flex-wrap">
              {["🇺🇸 Inglés","🇫🇷 Francés","🇩🇪 Alemán","🇮🇹 Italiano","🇵🇹 Portugués","🇯🇵 Japonés"].map((lang, i) => (
                <motion.span key={lang} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.5 + i * 0.06 }}
                  className="text-xs bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-full px-2.5 py-1 text-gray-600 dark:text-slate-300 shadow-sm">
                  {lang}
                </motion.span>
              ))}
              <span className="text-xs text-gray-400 dark:text-slate-500 self-center">+8 más</span>
            </div>
          </motion.div>

          {/* ── Desktop chat mockup ── */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block">
            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-xl shadow-gray-200/60 dark:shadow-slate-900/60">
              <div className="bg-gradient-to-r from-blue-100 to-orange-100 dark:from-blue-900/40 dark:to-orange-900/40 px-5 py-3 flex items-center justify-between border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">AI</div>
                  <div>
                    <p className="text-gray-700 dark:text-slate-200 text-sm font-medium">Lucía</p>
                    <p className="text-gray-500 dark:text-slate-400 text-xs">Tutora de español</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-gray-500 dark:text-slate-400 text-xs">En línea</span>
                </div>
              </div>
              <div className="p-5 space-y-4 bg-white dark:bg-slate-800">
                <div className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center text-xs shrink-0 mt-0.5">AI</div>
                  <div>
                    <div className="bg-[#F7F7F8] dark:bg-slate-700 rounded-2xl rounded-tl-md px-4 py-2.5">
                      <p className="text-sm text-gray-900 dark:text-slate-100">¡Hola! ¿Qué hiciste el fin de semana?</p>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 px-1">
                      <Volume2 className="w-3 h-3 text-gray-400 dark:text-slate-500" />
                      <div className="flex gap-px items-end">
                        {[4,8,6,10,5,9,7,11,4,8,6,3].map((h,i) => (
                          <div key={i} className="w-0.5 bg-blue-400/50 rounded-full" style={{ height: `${h}px` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-[#E8EFFF] dark:bg-blue-900/40 rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[80%]">
                    <p className="text-sm text-[#1D3B9D] dark:text-blue-300">Fui al cine con mis amigos.</p>
                  </div>
                </div>
                <div className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center text-xs shrink-0 mt-0.5">AI</div>
                  <div className="bg-[#F7F7F8] dark:bg-slate-700 rounded-2xl rounded-tl-md px-4 py-2.5">
                    <p className="text-sm text-gray-900 dark:text-slate-100">¡Genial! ¿Qué película vieron?</p>
                    <p className="text-xs text-blue-500 mt-1 cursor-pointer hover:text-blue-400 transition">Toca para traducir</p>
                  </div>
                </div>
                <div className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-500 flex items-center justify-center shrink-0 mt-0.5">
                    <Mic className="w-3 h-3" />
                  </div>
                  <div className="bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-2xl rounded-tl-md px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                      <span className="text-xs text-gray-500 dark:text-slate-400">Escuchando...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ─── SocialProofBar ─────────────────────────────────────────
function SocialProofBarES() {
  const stats = [
    { icon: <Globe className="w-4 h-4" />, value: "14", label: "Idiomas" },
    { icon: <Heart className="w-4 h-4" />, value: "En Vivo", label: "Conversaciones de Voz" },
    { icon: <ShieldOff className="w-4 h-4" />, value: "Gratis", label: "Para Probar" },
    { icon: <Clock className="w-4 h-4" />, value: "24/7", label: "Para Empezar" },
  ]
  return (
    <section className="bg-slate-900 border-t border-slate-800">
      <div className="max-w-5xl mx-auto px-6 py-4">
        <div className="flex flex-wrap justify-center gap-6 md:gap-12">
          {stats.map((stat, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="text-blue-400">{stat.icon}</div>
              <span className="text-white font-semibold text-sm">{stat.value}</span>
              <span className="text-slate-500 text-sm">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── LanguageCarousel ───────────────────────────────────────
const languageList = [
  { flag: "🇺🇸", name: "Inglés" }, { flag: "🇪🇸", name: "Español" },
  { flag: "🇫🇷", name: "Francés" }, { flag: "🇩🇪", name: "Alemán" },
  { flag: "🇮🇹", name: "Italiano" }, { flag: "🇵🇹", name: "Portugués" },
  { flag: "🇷🇺", name: "Ruso" }, { flag: "🇨🇳", name: "Chino" },
  { flag: "🇯🇵", name: "Japonés" }, { flag: "🇰🇷", name: "Coreano" },
  { flag: "🇳🇱", name: "Holandés" }, { flag: "🇵🇱", name: "Polaco" },
  { flag: "🇺🇦", name: "Ucraniano" }, { flag: "🇩🇰", name: "Danés" },
]

function LanguageCarouselES() {
  return (
    <section className="py-12 bg-slate-50 dark:bg-slate-900 border-y border-slate-200/60 dark:border-slate-700/60">
      <div className="max-w-5xl mx-auto px-6">
        <p className="text-center text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-6">
          Conversaciones de Voz en Vivo en 14 Idiomas:
        </p>
        <div className="flex flex-wrap justify-center gap-2.5">
          {languageList.map((lang, idx) => (
            <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-300 hover:border-blue-300 dark:hover:border-blue-600 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150">
              <span className="text-base">{lang.flag}</span>
              <span>{lang.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── HowItWorks ─────────────────────────────────────────────
function HowItWorksES() {
  const router = useRouter()
  const steps = [
    { number: "01", icon: <Globe className="w-6 h-6" />, title: "Elige un Idioma y Tus Intereses", desc: "Elige entre 14 idiomas y cuéntale a tu tutor de IA qué te gusta — música, viajes, historia, deportes, lo que sea. De eso vas a hablar.", accent: "from-blue-500 to-cyan-400" },
    { number: "02", icon: <Mic className="w-6 h-6" />, title: "Simplemente Habla — Los Errores Son Bienvenidos", desc: "Habla naturalmente usando tu micrófono. Equivocate en la gramática, olvidate de palabras, preguntá lo mismo 10 veces. A la IA no le importa — está para ayudar, no para juzgar.", accent: "from-blue-600 to-indigo-500" },
    { number: "03", icon: <TrendingUp className="w-6 h-6" />, title: "Mejorá Sin Darte Cuenta", desc: "Tu pronunciación mejora naturalmente — la IA solo te entiende cuando lo decís bien. Construís vocabulario con conversaciones reales, no con tarjetas.", accent: "from-indigo-500 to-blue-400" },
  ]
  return (
    <section className="relative py-24 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 overflow-hidden">
      {/* Subtle dot grid background */}
      <div className="absolute inset-0 opacity-[0.4] dark:opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle, #cbd5e1 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      {/* Soft gradient orbs */}
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-blue-100/60 dark:bg-blue-900/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-indigo-100/40 dark:bg-indigo-900/20 rounded-full blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-16">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-widest mb-3">Listo en 60 segundos</p>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white tracking-tight">Cómo Funciona Roset</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-4 max-w-lg mx-auto">Tres pasos simples para empezar a hablar un nuevo idioma con confianza.</p>
        </motion.div>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {/* Connecting line (desktop only) */}
          <div className="hidden md:block absolute top-[3.75rem] left-[calc(16.67%+2rem)] right-[calc(16.67%+2rem)] h-px">
            <div className="w-full h-full bg-gradient-to-r from-blue-200 via-blue-300 to-blue-200" />
            <div className="absolute top-1/2 left-0 w-2 h-2 -translate-y-1/2 rounded-full bg-blue-300" />
            <div className="absolute top-1/2 right-0 w-2 h-2 -translate-y-1/2 rounded-full bg-blue-300" />
          </div>

          {steps.map((step, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: idx * 0.15 }}
              className="group relative">
              <div className="relative bg-white dark:bg-slate-800/80 rounded-2xl p-8 h-full border border-slate-200/80 dark:border-slate-700/80 shadow-sm hover:shadow-xl hover:shadow-blue-100/50 dark:hover:shadow-blue-900/20 hover:border-blue-200 dark:hover:border-blue-700 transition-all duration-500">
                {/* Gradient top border accent */}
                <div className={`absolute top-0 left-6 right-6 h-0.5 rounded-full bg-gradient-to-r ${step.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                {/* Step number + icon row */}
                <div className="flex items-center gap-4 mb-6">
                  <div className={`relative flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${step.accent} shadow-lg shadow-blue-200/50`}>
                    <div className="text-white">{step.icon}</div>
                  </div>
                  <span className="text-5xl font-black text-slate-100 dark:text-slate-800 group-hover:text-blue-50 dark:group-hover:text-blue-900/30 transition-colors duration-300 select-none">{step.number}</span>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 leading-snug">{step.title}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }} className="text-center mt-14">
          <button onClick={() => { trackEvent("cta_click", { location: "how_it_works" }); router.push("/app") }}
            className="group relative inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3.5 rounded-full transition duration-200 shadow-lg shadow-blue-600/25 hover:shadow-blue-500/30">
            Prueba una Conversación Gratis
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
          </button>
        </motion.div>
      </div>
    </section>
  )
}

// ─── BenefitsSection ────────────────────────────────────────
function BenefitsSectionES() {
  const router = useRouter()
  const benefits = [
    {
      icon: <RefreshCw className="w-6 h-6" />, highlight: "24/7",
      title: "Tu Profesor Incansable 24/7",
      desc: "Un tutor que tiene paciencia infinita y sabe del tema que te interesa. Empezando el dia o tarde a la noche, Roset esta para ti.",
      features: ["Disponible 24 horas todos los días", "Paciencia infinita garantizada", "Experto en todos los temas de conversación"],
    },
    {
      icon: <Sparkles className="w-6 h-6" />, highlight: "Aprendizaje Divertido",
      title: "Aprender es Divertido",
      desc: "Habla de temas que realmente te interesan: películas, viajes, deportes, ciencia y más",
      features: ["Elige tus temas favoritos", "Flujo de conversación natural", "Aprendizaje disfrazado de diversión"],
    },
    {
      icon: <ShieldOff className="w-6 h-6" />, highlight: "Escenarios Reales",
      title: "Aumenta Tu Confianza",
      desc: "Practica situaciones reales como ir de compras, reservas de hotel o conversaciones en el aeropuerto.",
      features: ["Escenarios de restaurante y compras", "Práctica de reuniones de negocios", "Ensayos de situaciones de viaje"],
    },
    {
      icon: <Mic className="w-6 h-6" />, highlight: "Pronunciación Perfecta",
      title: "Mejora tu Pronunciación",
      desc: "La App transcribe de forma correcta cuando pronuncias bien, no te preocupes, puedes intentarlo tantas veces como necesites.",
      features: ["Retroalimentación instantánea de pronunciación", "Intentos de práctica ilimitados", "Ajuste progresivo de dificultad"],
    },
  ]
  return (
    <section className="py-24 bg-white dark:bg-slate-950 relative overflow-hidden">
      {/* Subtle background accents */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50/80 dark:bg-blue-900/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-50/60 dark:bg-indigo-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="text-center mb-16">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">Por qué Roset</p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">Todo lo que necesitas para hablar con fluidez</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {benefits.map((b, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="group relative rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/80 p-7 shadow-sm hover:border-blue-200 dark:hover:border-blue-700 hover:shadow-xl hover:shadow-blue-100/50 dark:hover:shadow-blue-900/20 transition-all duration-500">
              <div className="flex gap-5">
                <div className="shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/40 dark:to-blue-800/40 border border-blue-200/50 dark:border-blue-700/50 text-blue-600 dark:text-blue-400 flex items-center justify-center group-hover:from-blue-100 group-hover:to-blue-150 group-hover:border-blue-300/50 transition-all duration-500 group-hover:shadow-lg group-hover:shadow-blue-200/40 dark:group-hover:shadow-blue-900/40">
                    {b.icon}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 mb-3">
                    <span className="inline-block text-[11px] font-semibold uppercase tracking-widest text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 px-2.5 py-0.5 rounded-md">{b.highlight}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 tracking-tight">{b.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-5 leading-relaxed">{b.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {b.features.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700/50 border border-slate-200/80 dark:border-slate-600 rounded-lg px-2.5 py-1.5">
                        <Check className="w-3 h-3 text-blue-500" />
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-16">
          <button onClick={() => { trackEvent("cta_click", { location: "benefits" }); router.push("/app") }}
            className="group relative bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3.5 rounded-full transition duration-200 shadow-lg shadow-blue-600/25 hover:shadow-blue-500/30">
            <span className="flex items-center gap-2">
              Comienza a Hablar Sin Miedo Hoy
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
            </span>
          </button>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-4">No se requiere tarjeta de crédito &bull; Cancela cuando quieras &bull; Comienza a hablar en 60 segundos</p>
        </div>
      </div>
    </section>
  )
}

// ─── ProductDemo ────────────────────────────────────────────
function ProductDemoES() {
  const features = [
    { icon: <MessageCircle className="w-5 h-5" />, title: "Transcripción de Voz en Vivo", desc: "Habla naturalmente y ve tus palabras aparecer en tiempo real. Cuando la IA te entiende, sabes que tu pronunciación va por buen camino." },
    { icon: <Volume2 className="w-5 h-5" />, title: "Escucha Cómo Debe Sonar", desc: "Cada respuesta de la IA se pronuncia en voz alta con pronunciación nativa. Entrena tu oído escuchando e intenta igualarlo." },
    { icon: <Languages className="w-5 h-5" />, title: "Toca para Traducir Cualquier Palabra", desc: "¿Atascado en una palabra? Tócala para una traducción instantánea. Aprendes vocabulario en contexto, de conversaciones reales." },
    { icon: <BarChart3 className="w-5 h-5" />, title: "Tu Vocabulario Crece Naturalmente", desc: "Cuanto más hablas, más palabras aprendes. Revisa conversaciones pasadas y ve cómo tu vocabulario se expande sesión a sesión." },
  ]
  return (
    <section className="py-20 bg-white dark:bg-slate-950">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="text-center mb-14">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">Míralo en acción</p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">Una Conversación Real, No un Quiz</h2>
        </motion.div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="relative mx-auto w-full max-w-sm">
            <div className="bg-slate-900 rounded-[2rem] p-2.5 shadow-2xl shadow-slate-900/30">
              <div className="bg-white dark:bg-slate-800 rounded-[1.5rem] overflow-hidden">
                <div className="bg-blue-600 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-semibold">Y</div>
                    <p className="text-white text-sm font-medium">Yuki - Tutora de Japonés</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span className="text-blue-200 text-xs">En línea</span>
                  </div>
                </div>
                <div className="p-3.5 space-y-3 min-h-[280px] bg-slate-50 dark:bg-slate-900">
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">AI</div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm max-w-[82%]">
                      <p className="text-sm text-slate-800 dark:text-slate-200 leading-snug">¡Hola! ¿Viste algún buen anime recientemente? Yo acabo de terminar uno sobre cocina.</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Volume2 className="w-2.5 h-2.5 text-slate-400" />
                        <div className="flex gap-px items-end">
                          {[4,7,5,9,4,8,6,10,3,7].map((h,i) => <div key={i} className="w-0.5 bg-blue-400/60 rounded-full" style={{ height: `${h}px` }} />)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-blue-600 rounded-xl rounded-tr-sm px-3 py-2 max-w-[82%]">
                      <p className="text-sm text-white leading-snug">¡Sí! Vi Spy x Family. Fue muy divertido.</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">AI</div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm max-w-[82%]">
                      <p className="text-sm text-slate-800 dark:text-slate-200 leading-snug">¡Me encanta esa serie! ¿Quién es tu personaje favorito — Anya o Yor?</p>
                      <p className="text-xs text-blue-500 mt-0.5 cursor-pointer">Toca para traducir cualquier palabra</p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-blue-600 rounded-xl rounded-tr-sm px-3 py-2 max-w-[82%]">
                      <p className="text-sm text-white leading-snug">Anya es mi favorita. ¡Es muy tierna y graciosa!</p>
                    </div>
                  </div>
                </div>
                <div className="px-3 py-2.5 border-t border-slate-200 dark:border-slate-700 flex items-center gap-2.5">
                  <div className="flex-1 bg-slate-100 dark:bg-slate-700 rounded-full px-3.5 py-2">
                    <span className="text-xs text-slate-400 dark:text-slate-500">Toca para hablar o escribir...</span>
                  </div>
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-3.5 h-3.5 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          <div className="space-y-5">
            {features.map((f, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-30px" }} transition={{ duration: 0.3, delay: idx * 0.1 }} className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">{f.icon}</div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-0.5">{f.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}


// ─── PricingSection ─────────────────────────────────────────
function PricingSectionES() {
  const router = useRouter()

  const freeFeatures = ["Conversaciones de voz en vivo", "14 idiomas disponibles", "Transcripción en tiempo real", "Mensajes diarios limitados", "Temas de conversación básicos"]
  const premiumFeatures = ["Todo lo de Gratis, más:", "Conversaciones ilimitadas", "Todos los temas y escenarios", "Traducción con un clic en cualquier palabra", "Historial y memoria de conversaciones", "Seguimiento de progreso y analíticas", "Procesamiento de voz prioritario"]

  const monthlyPrice = 11.99
  const yearlyPrice = 59
  const yearlyMonthly = Math.round((yearlyPrice / 12) * 100) / 100
  const savingsPercent = Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100)

  return (
    <section className="py-20 bg-white dark:bg-slate-950">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="text-center mb-10">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">Precios</p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">Empieza Gratis, Sin Compromisos</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-3 max-w-lg mx-auto">Ten tu primera conversación hoy. Actualiza cuando quieras práctica ilimitada.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
            className="border border-slate-200 dark:border-slate-700 rounded-xl p-7">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Gratis</h3>
            <div className="mt-3 mb-5"><span className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight">$0</span></div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">Prueba conversaciones reales y siente cómo es practicar sin presión.</p>
            <button onClick={() => { trackEvent("cta_click", { location: "pricing_free" }); router.push("/app") }}
              className="w-full py-2.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition duration-150">
              Comienza Gratis
            </button>
            <ul className="mt-6 space-y-2.5">
              {freeFeatures.map((f, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-slate-400 dark:text-slate-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.1 }}
            className="border border-slate-200 dark:border-slate-700 rounded-xl p-7">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Mensual</h3>
            <div className="mt-3 mb-1">
              <span className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight">${monthlyPrice}</span>
              <span className="text-slate-500 dark:text-slate-400 text-sm ml-1">/mes</span>
            </div>
            <div className="mb-5" />
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">Conversaciones ilimitadas para quienes quieren practicar todos los días.</p>
            <button onClick={() => { trackEvent("cta_click", { location: "pricing_monthly" }); router.push("/app") }}
              className="w-full py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition duration-150">
              Comienza Gratis
            </button>
            <ul className="mt-6 space-y-2.5">
              {premiumFeatures.map((f, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.2 }}
            className="border-2 border-blue-600 rounded-xl p-7 relative bg-blue-50/30 dark:bg-blue-900/20">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-semibold px-3 py-0.5 rounded-full">Más Popular</div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Anual</h3>
            <div className="mt-3 mb-1">
              <span className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight">${yearlyMonthly}</span>
              <span className="text-slate-500 dark:text-slate-400 text-sm ml-1">/mes</span>
            </div>
            <p className="text-xs text-blue-600 font-medium mb-4">Facturado ${yearlyPrice}/año · Ahorra {savingsPercent}%</p>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">Conversaciones ilimitadas para quienes quieren practicar todos los días.</p>
            <button onClick={() => { trackEvent("cta_click", { location: "pricing_yearly" }); router.push("/app") }}
              className="w-full py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition duration-150">
              Comienza Gratis
            </button>
            <ul className="mt-6 space-y-2.5">
              {premiumFeatures.map((f, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  )
}


// ─── FaqSection ─────────────────────────────────────────────
const faqs = [
  { question: "¿Qué idiomas admite Roset para conversaciones de voz en vivo?", answer: "Roset admite conversaciones de voz en vivo con transcripción en tiempo real en 14 idiomas: 🇺🇸 Inglés, 🇪🇸 Español, 🇫🇷 Francés, 🇩🇪 Alemán, 🇮🇹 Italiano, 🇵🇹 Portugués, 🇷🇺 Ruso, 🇨🇳 Chino, 🇯🇵 Japonés, 🇰🇷 Coreano, 🇳🇱 Holandés, 🇵🇱 Polaco, 🇺🇦 Ucraniano y 🇩🇰 Danés. Puedes mantener conversaciones naturales con tu tutor de IA usando tu voz en cualquiera de estos idiomas." },
  { question: "¿Cómo funciona la transcripción en vivo de Roset?", answer: "Roset utiliza tecnología avanzada de Web Speech API para la conversión instantánea de voz a texto. Simplemente habla de forma natural y observa cómo aparecen tus palabras en tiempo real mientras hablas. Nuestro tutor de IA responde inmediatamente, creando un flujo de conversación natural. El sistema funciona directamente en tu navegador sin necesidad de descargas adicionales." },
  { question: "¿Qué hace diferente a Roset de otras aplicaciones de aprendizaje de idiomas?", answer: "A diferencia de las aplicaciones de idiomas tradicionales, Roset ofrece conversaciones de voz en tiempo real con tutores de IA que se adaptan a tu estilo de aprendizaje, objetivos y ritmo. Puedes hablar de forma natural y obtener retroalimentación instantánea, practicar pronunciación y mantener conversaciones significativas. Nuestra IA personaliza las lecciones basándose en tus intereses y progreso de aprendizaje." },
  { question: "¿Puedo aprender múltiples idiomas al mismo tiempo?", answer: "¡Sí! Puedes cambiar entre cualquiera de nuestros idiomas compatibles y practicar con tutores de IA nativos especializados para cada idioma. Cada tutor se adapta a tu nivel de competencia en ese idioma específico, permitiéndote mantener el progreso en múltiples idiomas simultáneamente." },
  { question: "¿Qué dispositivos y navegadores admiten las funciones de voz de Roset?", answer: "La transcripción de voz en vivo de Roset funciona en dispositivos de escritorio y móviles. Para la mejor experiencia, usa Chrome en Android o Safari en iPhone/iPad. La plataforma también funciona en navegadores de escritorio Chrome, Edge y Safari. Se requiere una conexión a internet para la transcripción en vivo." },
  { question: "¿Es Roset adecuado para principiantes completos?", answer: "¡Por supuesto! Roset está diseñado para guiarte desde tus primeras palabras. Nuestros tutores de IA comienzan con conceptos básicos como saludos y frases comunes, luego introducen gradualmente conceptos más complejos. El sistema de aprendizaje adaptativo asegura que nunca te sientas abrumado y siempre desafiado al nivel correcto." },
  { question: "¿Cuánto cuesta Roset?", answer: "Roset ofrece una prueba gratuita para que puedas experimentar el aprendizaje con conversaciones en vivo antes de comprometerte. Después de tu prueba, ofrecemos planes premium flexibles con conversaciones ilimitadas, funciones avanzadas y acceso a tutores especializados. Consulta nuestra página de precios para tarifas actuales y ofertas especiales." },
  { question: "¿Puedo practicar temas o situaciones específicas?", answer: "¡Sí! Nuestros tutores de IA pueden adaptar las conversaciones para enfocarse en temas específicos como reuniones de negocios, escenarios de viaje, conversaciones casuales o discusiones académicas. Simplemente dile a tu tutor qué te gustaría practicar, y crearán conversaciones relevantes y atractivas adaptadas a tus intereses." },
  { question: "¿Por qué debería elegir Roset en lugar de ChatGPT para el aprendizaje de idiomas?", answer: "comparison" },
]

function FaqSectionES() {
  const [openIndex, setOpenIndex] = useState(null)
  const router = useRouter()

  return (
    <section className="py-20 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="text-center mb-12">
          <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">Todo lo que necesitas saber</p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">Preguntas Frecuentes</h2>
        </motion.div>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-20px" }} transition={{ duration: 0.3, delay: index * 0.05 }}
              className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex justify-between items-center p-5 text-left hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors duration-150">
                <span className="text-sm font-medium text-slate-900 dark:text-white pr-4">{faq.question}</span>
                <motion.div animate={{ rotate: openIndex === index ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-500 shrink-0" />
                </motion.div>
              </button>
              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2, ease: "easeInOut" }} className="overflow-hidden">
                    <div className="px-5 pb-5">
                      {faq.answer === "comparison" ? (
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg -mx-1 p-0"><GeneralRosetVsGpt /></div>
                      ) : (
                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{faq.answer}</p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <button onClick={() => { trackEvent("cta_click", { location: "faq" }); router.push("/app") }}
            className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-3 rounded-full transition duration-200 hover:shadow-lg hover:shadow-blue-600/25">
            ¿Tienes más preguntas?
          </button>
        </div>
      </div>
    </section>
  )
}

// ─── FinalCTA ───────────────────────────────────────────────
function FinalCTAES() {
  const router = useRouter()
  return (
    <section className="py-16 bg-slate-950">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">¿Listo para empezar a hablar?</h2>
        <p className="text-slate-400 mt-3 max-w-md mx-auto">Únete a estudiantes que practican conversaciones reales en 14 idiomas. Gratis para empezar, sin tarjeta de crédito.</p>
        <button onClick={() => { trackEvent("cta_click", { location: "final_cta" }); router.push("/app") }}
          className="group inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold text-lg px-8 py-4 rounded-full mt-8 transition duration-200 hover:shadow-lg hover:shadow-blue-600/25">
          Comienza a Hablar Gratis
          <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform duration-200" />
        </button>
      </motion.div>
    </section>
  )
}

// ─── Footer ─────────────────────────────────────────────────
function FooterES() {
  return (
    <footer className="bg-slate-950 text-slate-400">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <Logo />
              <span className="text-lg font-semibold text-white">Roset</span>
            </div>
            <p className="text-sm leading-relaxed max-w-xs">Practica idiomas con conversaciones de voz impulsadas por IA. 14 idiomas, disponible 24/7.</p>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-4">Producto</h4>
            <ul className="space-y-2.5">
              <li><a href="/app" className="text-sm hover:text-white transition-colors duration-150">Empezar a Aprender</a></li>
              <li><a href="/contact" className="text-sm hover:text-white transition-colors duration-150">Contactar a Roset</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-4">Legal</h4>
            <ul className="space-y-2.5">
              <li><a href="/privacypolicy" className="text-sm hover:text-white transition-colors duration-150">Términos y Política de Privacidad</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} Roset, Inc.</p>
          <a href="https://wa.me/+4552803868" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-white transition-colors duration-150">WhatsApp</a>
        </div>
      </div>
    </footer>
  )
}

// ─── Main Page ──────────────────────────────────────────────
export default function LandingPageES() {
  // Ensure users coming from this Spanish landing page get Spanish in the app
  useEffect(() => {
    try { localStorage.setItem("appLang", "es") } catch {}
  }, [])

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col overflow-x-hidden">
      <ScrollDepthTracker />
      <WhatsAppWidget className="fixed bottom-6 right-6 z-50" />
      <TopMenuES />
      <HeroSectionES />
      <SocialProofBarES />
      <LanguageCarouselES />
      <div id="how-it-works-section"><HowItWorksES /></div>
      <div id="benefits-section"><BenefitsSectionES /></div>
      <ProductDemoES />
      <div id="pricing-section"><PricingSectionES /></div>
      <div id="faq-section"><FaqSectionES /></div>
      <FinalCTAES />
      <FooterES />
    </div>
  )
}
